import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  Rating as MuiRating,
  alpha,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Star, Close, Coffee } from '@mui/icons-material';

interface RatingModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number, feedback: string, visitId: string) => Promise<void>;
  cafeName: string;
  visitedAt: string;
  userName: string;
  darkMode: boolean;
  visitId: string;
}

const RatingModal: React.FC<RatingModalProps> = ({
  open,
  onClose,
  onSubmit,
  cafeName,
  visitedAt,
  userName,
  darkMode,
  visitId
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  
  const handleSubmit = async () => {
    if (!rating || !visitId) return;
    
    setSubmitting(true);
    try {
      await onSubmit(rating, feedback, visitId);
      setRating(null);
      setFeedback('');
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          bgcolor: darkMode ? '#0F1A15' : '#fff',
          border: `1px solid ${alpha(goldColor, 0.3)}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          color: darkMode ? '#fff' : deepGreen
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Coffee sx={{ color: goldColor, fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Rate Your Experience
            </Typography>
            <Typography variant="caption" sx={{ 
              color: darkMode ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
              display: 'block',
              mt: 0.5
            }}>
              Help us improve your future cafe visits
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: darkMode ? '#fff' : deepGreen,
            '&:hover': { bgcolor: alpha(goldColor, 0.1) }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {/* Welcome Back Section */}
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: '12px',
          bgcolor: darkMode ? alpha('#fff', 0.05) : alpha(deepGreen, 0.03),
          border: `1px solid ${alpha(goldColor, 0.2)}`
        }}>
          <Typography variant="body1" sx={{ 
            color: darkMode ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8),
            fontWeight: 500 
          }}>
            Welcome back, <Box component="span" sx={{ color: goldColor, fontWeight: 600 }}>{userName}</Box>!
          </Typography>
          <Typography variant="caption" sx={{ 
            color: darkMode ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
            display: 'block', 
            mt: 0.5 
          }}>
            You visited this cafe on {formatDate(visitedAt)}
          </Typography>
        </Box>
        
        {/* Cafe Info */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ 
            color: goldColor, 
            fontWeight: 700,
            mb: 0.5
          }}>
            {cafeName}
          </Typography>
          <Typography variant="body2" sx={{ 
            color: darkMode ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
            fontStyle: 'italic'
          }}>
            How was your experience?
          </Typography>
        </Box>
        
        {/* Rating Section */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ 
            mb: 2, 
            fontWeight: 600,
            color: darkMode ? '#fff' : deepGreen
          }}>
            Rate your visit
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <MuiRating
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
              size="large"
              icon={<Star sx={{ fontSize: 40, color: goldColor }} />}
              emptyIcon={<Star sx={{ 
                fontSize: 40, 
                color: darkMode ? alpha('#fff', 0.3) : alpha(deepGreen, 0.3) 
              }} />}
            />
            {rating && (
              <Typography variant="h6" sx={{ color: goldColor, minWidth: 60 }}>
                {rating}/5
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ 
              color: darkMode ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5) 
            }}>
              Poor
            </Typography>
            <Typography variant="caption" sx={{ 
              color: darkMode ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5) 
            }}>
              Excellent
            </Typography>
          </Box>
        </Box>
        
        {/* Feedback Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ 
            mb: 1, 
            fontWeight: 600,
            color: darkMode ? '#fff' : deepGreen
          }}>
            Share your thoughts (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="What did you like? Any suggestions for improvement?"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: darkMode ? alpha('#fff', 0.05) : alpha(deepGreen, 0.02),
                '& fieldset': {
                  borderColor: darkMode ? alpha(goldColor, 0.3) : alpha(deepGreen, 0.2)
                },
                '&:hover fieldset': {
                  borderColor: goldColor
                }
              }
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          sx={{
            color: darkMode ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8),
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            '&:hover': {
              bgcolor: darkMode ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05)
            }
          }}
        >
          Skip for now
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!rating || submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <Star />}
          sx={{
            bgcolor: goldColor,
            color: deepGreen,
            fontWeight: 700,
            borderRadius: '12px',
            px: 4,
            boxShadow: `0 4px 20px ${alpha(goldColor, 0.3)}`,
            '&:hover': {
              bgcolor: alpha(goldColor, 0.9),
              boxShadow: `0 6px 25px ${alpha(goldColor, 0.4)}`
            },
            '&.Mui-disabled': {
              bgcolor: darkMode ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1),
              color: darkMode ? alpha('#fff', 0.3) : alpha(deepGreen, 0.3)
            }
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatingModal;